import {
  ArtistRow,
  type Timeframe,
  type TopArtist,
} from "@components/Account/You/TopArtistsList";
import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import {
  groupArtistsByFanLevel,
  type FanLevelGroup,
} from "@components/Account/You/fanLevelGroups";
import { useGamificationSummary } from "@hooks/trpc/useGamificationSummary";
import { useMe } from "@hooks/trpc/useMe";
import { FlashList } from "@shopify/flash-list";
import { BottomSheetHandle } from "@utils/swmModal/components/BottomSheetPrimitives";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import { APP_MODAL_CORNER_RADIUS } from "@utils/appModal/constants";
import { forwardRef, useCallback, useMemo, useState } from "react";
import { StyleSheet, useWindowDimensions, View } from "react-native";
import {
  type SheetAdapterRef,
  useBottomSheetManager,
} from "react-native-bottom-sheet-stack";

const DarkSheetSurface = () => (
  <View style={[StyleSheet.absoluteFill, styles.darkSurface]} />
);

const LIST_VIEWPORT_HEIGHT_RATIO = 0.58;
const LIST_VIEWPORT_MAX_HEIGHT = 520;

type FanLevelListItem =
  | { type: "level"; group: FanLevelGroup }
  | { type: "artist"; artist: TopArtist; rank: number };

const YouArtistsModalSWM = () => {
  const summaryQuery = useGamificationSummary();
  const summary = summaryQuery.data ?? null;
  const { query: meQuery } = useMe();
  const bottomSheetManager = useBottomSheetManager();
  const { height: windowHeight } = useWindowDimensions();
  const currencySymbol = meQuery.data?.plan?.currencySymbol ?? "£";
  const timeframe: Timeframe = "allTime";
  const [collapsedLevels, setCollapsedLevels] = useState<Set<number>>(
    () => new Set(),
  );

  const activeArtists = useMemo(() => {
    return summary?.artistStatuses ?? [];
  }, [summary?.artistStatuses]);

  const levelGroups = useMemo(
    () => groupArtistsByFanLevel(activeArtists),
    [activeArtists],
  );

  const listItems = useMemo<FanLevelListItem[]>(
    () =>
      levelGroups.flatMap((group) => [
        { type: "level" as const, group },
        ...(collapsedLevels.has(group.level)
          ? []
          : group.artists.map(({ artist, rank }) => ({
              type: "artist" as const,
              artist,
              rank,
            }))),
      ]),
    [collapsedLevels, levelGroups],
  );

  const toggleLevel = useCallback((level: number) => {
    setCollapsedLevels((current) => {
      const next = new Set(current);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  }, []);

  const renderItem = useCallback(
    ({ item }: { item: FanLevelListItem }) => {
      if (item.type === "level") {
        const isCollapsed = collapsedLevels.has(item.group.level);
        return (
          <LPressable
            accessibilityLabel={`${item.group.levelName}, ${item.group.artists.length} artists`}
            accessibilityRole="button"
            onPress={() => toggleLevel(item.group.level)}
            sentryEventName="profile-fan-level-toggle"
          >
            <LBox
              alignItems="center"
              flexDirection="row"
              justifyContent="space-between"
              pb="s2"
              pt="s3"
            >
              <LBox flexDirection="row" alignItems="center" gap="s2">
                <LText variant="Heading/H2/Bold">{item.group.levelName}</LText>
                <LText color="subText">{item.group.artists.length}</LText>
              </LBox>
              <LText color="subText">{isCollapsed ? "▼" : "▲"}</LText>
            </LBox>
          </LPressable>
        );
      }

      return (
        <ArtistRow
          artist={item.artist}
          currencySymbol={currencySymbol}
          isInModal
          onCloseModal={bottomSheetManager.closeAll}
          rank={item.rank}
          timeframe={timeframe}
        />
      );
    },
    [
      bottomSheetManager.closeAll,
      collapsedLevels,
      currencySymbol,
      timeframe,
      toggleLevel,
    ],
  );

  const keyExtractor = useCallback((item: FanLevelListItem) => {
    return item.type === "level"
      ? `level-${item.group.level}`
      : `artist-${item.artist.artistId}`;
  }, []);

  const listViewportHeight = Math.min(
    windowHeight * LIST_VIEWPORT_HEIGHT_RATIO,
    LIST_VIEWPORT_MAX_HEIGHT,
  );

  return (
    <LBox pb="l1">
      <BottomSheetHandle backgroundColor="whiteTranslucent70" />
      <LBox px="s4" pt="s2">
        <LBox
          alignItems="center"
          flexDirection="row"
          justifyContent="space-between"
          mb="m1"
        >
          <LText variant="Heading/H1/Bold">Your Top Artists</LText>
        </LBox>

        {summaryQuery.isLoading ? (
          <LBox flex={1} alignItems="center" justifyContent="center" py="l1">
            <LText color="subText">Loading...</LText>
          </LBox>
        ) : activeArtists.length > 0 ? (
          <View style={{ height: listViewportHeight }}>
            <FlashList
              data={listItems}
              ItemSeparatorComponent={() => <LBox pb="s3" />}
              keyExtractor={keyExtractor}
              ListFooterComponent={<LBox height={24} />}
              renderItem={renderItem}
              showsVerticalScrollIndicator={false}
            />
          </View>
        ) : (
          <LBox flex={1} alignItems="center" justifyContent="center" py="l1">
            <LText color="subText">No artists yet. Start streaming!</LText>
          </LBox>
        )}
      </LBox>
    </LBox>
  );
};

export const YouArtistsModalSWMSheet = forwardRef<SheetAdapterRef>(
  function YouArtistsModalSWMSheet(_props, ref) {
    return (
      <AppModalSheet
        ref={ref}
        showHandle={false}
        surface={<DarkSheetSurface />}
      >
        <YouArtistsModalSWM />
      </AppModalSheet>
    );
  },
);

export default YouArtistsModalSWM;

const styles = StyleSheet.create({
  darkSurface: {
    backgroundColor: "#17171C",
    borderTopLeftRadius: APP_MODAL_CORNER_RADIUS,
    borderTopRightRadius: APP_MODAL_CORNER_RADIUS,
    overflow: "hidden",
  },
});
